import React, { useState, useEffect } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import '../styles/consultation-form.css';
import { sendConsultationEmail } from '../utils/email-service';

const ConsultationForm = ({ onClose, projectType }) => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectType: '',
    projectDetails: '',
    preferredDate: '',
    preferredTime: 'morning'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  
  // Check authentication status when component mounts
  useEffect(() => {
    setIsAuthenticated(!!user);
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      setAuthError(true);
      setError('You must be signed in to submit a consultation request.');
      setIsSubmitting(false);
      return;
    }
    
    try {
      // Send the consultation form data via email
      const formDataWithType = {
        ...formData,
        projectType: formatProjectType(projectType)
      };
      
      await sendConsultationEmail(formDataWithType);
      
      // Log the submission for debugging
      console.log('Consultation form submitted:', formDataWithType);
      
      setIsSubmitted(true);
    } catch (err) {
      console.error('Error submitting consultation form:', err);
      setError('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isSubmitted) {
    return (
      <div className="consultation-form-container">
        <div className="consultation-success">
          <h3>Thank You!</h3>
          <p>Your consultation request has been submitted successfully and sent to info@relufox.ai. One of our experts will contact you within 1 business day to schedule your free consultation.</p>
          <button className="close-button" onClick={onClose || (() => window.history.back())}>Close</button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="consultation-form-container">
      <div className="consultation-form-header">
        <h3>Request a Free Consultation</h3>
        <p className="consultation-phone">Or call us directly: <strong>(630) 940-8837</strong></p>
        <button className="close-icon" onClick={onClose || (() => window.history.back())}>×</button>
      </div>
      
      {authError && (
        <div className="auth-error-message">
          <p>You need to be signed in to request a consultation.</p>
          <p>Please <a href="/login">sign in</a> or <a href="/signup">create an account</a> to continue.</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="consultation-form">
        <div className="form-group">
          <label htmlFor="name">Full Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email Address *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="company">Company Name</label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="projectType">Project Type (optional)</label>
          <input
            type="text"
            id="projectType"
            name="projectType"
            value={formData.projectType}
            onChange={handleChange}
            placeholder="e.g., Data Analytics, Chatbot, Custom AI Solution"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="projectDetails">Project Details (optional)</label>
          <textarea
            id="projectDetails"
            name="projectDetails"
            value={formData.projectDetails}
            onChange={handleChange}
            rows="4"
            placeholder="Tell us about your project, goals, and any specific requirements"
          ></textarea>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="preferredDate">Preferred Date</label>
            <input
              type="date"
              id="preferredDate"
              name="preferredDate"
              value={formData.preferredDate}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="preferredTime">Preferred Time</label>
            <select
              id="preferredTime"
              name="preferredTime"
              value={formData.preferredTime}
              onChange={handleChange}
            >
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="evening">Evening</option>
            </select>
          </div>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="form-actions">
          <button 
            type="submit" 
            className="submit-button" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Request Consultation'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Helper function to format project type for display
const formatProjectType = (type) => {
  switch (type) {
    case 'inventory':
      return 'Inventory Management System';
    case 'document_ai':
      return 'Document AI Solution';
    case 'computer_vision':
      return 'Computer Vision Solution';
    case 'genai_agent':
      return 'GenAI Agent';
    case 'chatbot':
      return 'Chatbot';
    case 'analytics':
      return 'Data Analytics Solution';
    case 'recommendation':
      return 'Recommendation System';
    default:
      return 'Custom AI Solution';
  }
};

export default ConsultationForm;